import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import { GarmRiskLevelType, CategoryType } from '@insights_module/types/BrandSafety';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { CircleExclamationMarkIcon } from '@icons/CircleExclamationMarkIcon';

const Bar = tw.div`w-[3px] rounded-[1px]`;
const LevelIndicatorWrapper = tw.div`min-w-[15px] min-h-[14px] flex justify-center items-center rounded-[3px]`;
const LevelIndicatorContainer = tw.div`flex flex-row gap-[1px] items-baseline`;
const BadgeWrapper = tw.div`max-w-[100px] h-[48px] flex flex-row gap-2 rounded items-center justify-center px-[16px]`;
export const Title = tw.div`text-[11px] font-semibold text-text-and-icon whitespace-nowrap`;
const TitleFull = tw.div`text-[11px] font-normal whitespace-nowrap`;
const RiskContainer = tw.div`text-center rounded text-[10px] font-bold px-1 py-0.5 whitespace-nowrap`;
const NoIconWrapper = tw.div`max-w-[180px] text-center rounded text-[10px] font-bold px-1 py-0.5 truncate`;
const InnerWrapper = tw.div`flex flex-row gap-2 items-center justify-center px-[8px] py-[6px] bg-neutral-background rounded border w-[84px] min-w-[84px] max-w-[84px] text-center`;

const INDICATOR_BARS = {
  FIRST: 'first-bar',
  SECOND: 'second-bar',
  THIRD: 'third-bar',
  FOURTH: 'fourth-bar',
};

const INDICATOR_BACKGROUND_COLOR = 'bg-indicator-color';

const RISK_LEVEL_MAPPER = {
  [GARM_RISK_LEVEL.unrelated]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-unrelated-muted`,
    [INDICATOR_BACKGROUND_COLOR]: tw`bg-transparent`,
    border: tw`border-garm-unrelated-muted`,
    text: tw`text-text-and-icon font-medium`,
  },
  [GARM_RISK_LEVEL.low]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-low-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-low-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-low-muted`,
    [INDICATOR_BACKGROUND_COLOR]: tw`bg-garm-low-bg`,
    border: tw`border-garm-low-muted`,
    text: tw`text-text-and-icon font-medium`,
  },
  [GARM_RISK_LEVEL.medium]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-medium-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-medium-muted`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-medium-muted`,
    [INDICATOR_BACKGROUND_COLOR]: tw`bg-garm-medium-bg`,
    border: tw`border-garm-medium-muted`,
    text: tw`text-text-and-icon font-medium`,
  },
  [GARM_RISK_LEVEL.high]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-high-dark`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-high-muted`,
    [INDICATOR_BACKGROUND_COLOR]: tw`bg-garm-high-bg`,
    border: tw`border-garm-high-muted`,
    text: tw`text-text-and-icon font-medium`,
  },
  [GARM_RISK_LEVEL.floor]: {
    [INDICATOR_BARS.FIRST]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.SECOND]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.THIRD]: tw`bg-garm-floor-dark`,
    [INDICATOR_BARS.FOURTH]: tw`bg-garm-floor-dark`,
    [INDICATOR_BACKGROUND_COLOR]: tw`bg-floor-bg`,
    border: tw`border-garm-floor-muted`,
    text: tw`text-text-and-icon font-medium`,
  },
};

const EPISODE_DETAILS_RISK_LEVEL_MAPPER = {
  [GARM_RISK_LEVEL.low]: tw`bg-garm-low-bg text-text-and-icon-invert`,
  [GARM_RISK_LEVEL.medium]: tw`bg-garm-medium-bg text-text-and-icon-invert`,
  [GARM_RISK_LEVEL.high]: tw`bg-garm-high-bg text-text-and-icon-invert`,
  [GARM_RISK_LEVEL.floor]: tw`bg-floor-bg text-text-and-icon-invert`,
};

export const LevelIndicator = ({
  level,
  unrelatedAsCheckmark = false,
  ...rest
}: {
  level: GarmRiskLevelType;
  unrelatedAsCheckmark?: boolean;
}) => {
  if (level === GARM_RISK_LEVEL.unrelated && unrelatedAsCheckmark) {
    return (
      <LevelIndicatorWrapper {...rest}>
        <CheckMarkIcon tw='stroke-garm-unrelated-dark' />
      </LevelIndicatorWrapper>
    );
  }

  return (
    <LevelIndicatorWrapper {...rest}>
      <LevelIndicatorContainer>
        <Bar tw='h-[6px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.FIRST]} />
        <Bar tw='h-[9px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.SECOND]} />
        <Bar tw='h-[12px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.THIRD]} />
        <Bar tw='h-[14px]' css={RISK_LEVEL_MAPPER[level][INDICATOR_BARS.FOURTH]} />
      </LevelIndicatorContainer>
    </LevelIndicatorWrapper>
  );
};

const GarmRiskBadge = ({
  category,
  risk,
  ...rest
}: {
  category: CategoryType;
  risk?: GarmRiskLevelType;
}) => {
  const { t } = useTranslation();
  if (!risk)
    return (
      <BadgeWrapper data-testid='garm-badge-no-data' tw='bg-transparent' {...rest}>
        <InnerWrapper tw='border-gray-1'>
          <Title>{t('insights.garm_risk_level.no_data')}</Title>
        </InnerWrapper>
      </BadgeWrapper>
    );

  return (
    <BadgeWrapper
      data-testid={`garm-badge-${category + '-' + risk}`}
      css={[RISK_LEVEL_MAPPER[risk][INDICATOR_BACKGROUND_COLOR]]}
      {...rest}>
      <InnerWrapper css={[RISK_LEVEL_MAPPER[risk].border]}>
        <LevelIndicator level={risk} />
        <Title>
          {risk !== GARM_RISK_LEVEL.unrelated
            ? t(`insights.garm_risk_level.${risk}_short`)
            : t('insights.garm_risk_level.no_risk')}
        </Title>
      </InnerWrapper>
    </BadgeWrapper>
  );
};

const Safe = ({ ...rest }) => {
  const { t } = useTranslation();
  return (
    <BadgeWrapper
      tw='w-[154px] h-[21px] bg-garm-unrelated-muted overflow-visible'
      data-testid='garm-badge-safe'
      title={t('insights.garm_all_safe')}
      {...rest}>
      <CheckMarkIcon tw='stroke-garm-unrelated-dark' />
      <TitleFull css={RISK_LEVEL_MAPPER[GARM_RISK_LEVEL.unrelated]['text']}>
        {t('insights.garm_all_safe')}
      </TitleFull>
    </BadgeWrapper>
  );
};

const Error = () => {
  const { t } = useTranslation();
  return (
    <BadgeWrapper
      tw='w-[342px] h-[21px] bg-error-transparent'
      data-testid='garm-badge-error'
      title={t('insights.garm_error')}>
      <CircleExclamationMarkIcon tw='stroke-error' />
      <Title tw='text-error'>{t('insights.garm_error')}</Title>
    </BadgeWrapper>
  );
};

const Risk = ({ risk }: { risk: GarmRiskLevelType }) => {
  const { t } = useTranslation();

  return (
    <RiskContainer css={[EPISODE_DETAILS_RISK_LEVEL_MAPPER[risk]]}>
      {t(`insights.garm_risk_level.${risk}`)}
    </RiskContainer>
  );
};

const NoIcon = ({ category, risk }: { category: CategoryType; risk: GarmRiskLevelType }) => {
  const { t } = useTranslation();

  return (
    <NoIconWrapper
      css={[EPISODE_DETAILS_RISK_LEVEL_MAPPER[risk]]}
      title={t(`insights.garm_category.${category}`)}>
      {t(`insights.garm_category.${category}`)}
    </NoIconWrapper>
  );
};

GarmRiskBadge.Safe = Safe;
GarmRiskBadge.Error = Error;
GarmRiskBadge.Risk = Risk;
GarmRiskBadge.NoIcon = NoIcon;

export { GarmRiskBadge };
